import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { AddStaffModal } from '../../components/AddStaffModal';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';

interface StaffUser {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: string;
  role: string;
  phone: string;
  last_login: string;
  created_at: string;
}

export const StaffList: React.FC = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStaff();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [staff, statusFilter, roleFilter, searchQuery]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/admin/staff`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch staff');
      }

      const data = await response.json();
      setStaff(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...staff];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(s => s.role === roleFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.first_name.toLowerCase().includes(query) ||
        s.last_name.toLowerCase().includes(query) ||
        s.email.toLowerCase().includes(query)
      );
    }

    setFilteredStaff(filtered);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin': return 'badge-error';
      case 'loan_officer': return 'badge-primary';
      case 'accountant': return 'badge-success';
      case 'customer_service': return 'badge-info';
      default: return 'badge-secondary';
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: { [key: string]: string } = {
      'admin': 'Administrator',
      'loan_officer': 'Loan Officer',
      'accountant': 'Accountant',
      'customer_service': 'Customer Service',
      'staff': 'Staff'
    };
    return labels[role] || role;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Staff Management</h1>
          <p className="page-description">
            Manage staff users and their roles
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
        >
          + Add Staff User
        </Button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="search">Search</label>
            <input
              id="search"
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="role">Role</label>
            <select
              id="role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="select"
            >
              <option value="all">All Roles</option>
              <option value="admin">Administrator</option>
              <option value="loan_officer">Loan Officer</option>
              <option value="accountant">Accountant</option>
              <option value="customer_service">Customer Service</option>
              <option value="staff">Staff</option>
            </select>
          </div>
        </div>

        <div className="results-count">
          Showing {filteredStaff.length} of {staff.length} staff users
        </div>
      </div>

      {/* Staff Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '40px' }}>
                  No staff users found
                </td>
              </tr>
            ) : (
              filteredStaff.map((staffUser) => (
                <tr key={staffUser.user_id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {staffUser.first_name[0]}{staffUser.last_name[0]}
                      </div>
                      <div>
                        <div className="user-name">
                          {staffUser.first_name} {staffUser.last_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{staffUser.email}</td>
                  <td>{staffUser.phone || 'N/A'}</td>
                  <td>
                    <span className={`badge ${getRoleBadgeClass(staffUser.role)}`}>
                      {getRoleLabel(staffUser.role)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${
                      staffUser.status === 'active' ? 'badge-success' :
                      staffUser.status === 'inactive' ? 'badge-secondary' :
                      'badge-warning'
                    }`}>
                      {staffUser.status}
                    </span>
                  </td>
                  <td>{formatDate(staffUser.last_login)}</td>
                  <td>{formatDate(staffUser.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <Button
                        onClick={() => navigate(`/admin/staff/${staffUser.user_id}`)}
                        variant="secondary"
                        size="sm"
                      >
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Staff Modal */}
      {showAddModal && (
        <AddStaffModal
          onClose={() => setShowAddModal(false)}
          onSuccess={fetchStaff}
        />
      )}

      <style>{`
        .filters-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .filters-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-group label {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        .results-count {
          font-size: 14px;
          color: #6b7280;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .user-name {
          font-weight: 500;
          color: #1f2937;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 32px;
          color: #9ca3af;
          cursor: pointer;
          line-height: 1;
          padding: 0;
          width: 32px;
          height: 32px;
        }

        .modal-close:hover {
          color: #4b5563;
        }

        .modal-body {
          padding: 24px;
        }

        @media (max-width: 968px) {
          .filters-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
